import { LitElement, html, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { COUNTRIES, flagUrl, getCountry, type Country } from './countries.js';
import { formatAsYouType, isValidE164, toE164 } from './format.js';

export interface PhoneInputChangeDetail {
  /** E.164 formatted phone number, e.g. "+886912345678". Empty string if not parseable. */
  phone: string;
  /** iso2 country code, e.g. "tw". */
  country: string;
  /** Raw user input (formatted national number). */
  inputValue: string;
  /** Whether `phone` is a valid phone number. */
  isValid: boolean;
}

/**
 * International phone number input with inline country selector.
 *
 * @element lightup-phone-input
 * @fires change - Detail: PhoneInputChangeDetail
 */
@customElement('lightup-phone-input')
export class LightupPhoneInput extends LitElement {
  static override styles = css`
    :host {
      display: block;
      font-family:
        'Inter',
        system-ui,
        -apple-system,
        'Segoe UI',
        Roboto,
        sans-serif;
      font-size: 14px;
      color: #1f2937;
      --lp-border: #d1d5db;
      --lp-border-focus: #2563eb;
      --lp-border-error: #dc2626;
      --lp-bg: #fff;
      --lp-bg-hover: #f3f4f6;
      --lp-text-muted: #6b7280;
      position: relative;
    }

    :host([disabled]) {
      opacity: 0.55;
      pointer-events: none;
    }

    .wrap {
      display: flex;
      align-items: center;
      border: 1px solid var(--lp-border);
      border-radius: 8px;
      background: var(--lp-bg);
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }

    .wrap:focus-within {
      border-color: var(--lp-border-focus);
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }

    :host([error]) .wrap {
      border-color: var(--lp-border-error);
    }
    :host([error]) .wrap:focus-within {
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
    }

    button.flag-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0 8px 0 10px;
      background: transparent;
      border: 0;
      border-right: 1px solid var(--lp-border);
      cursor: pointer;
      height: 36px;
      font: inherit;
      color: inherit;
      border-radius: 8px 0 0 8px;
    }
    button.flag-btn:hover {
      background: var(--lp-bg-hover);
    }
    button.flag-btn:focus-visible {
      outline: 2px solid var(--lp-border-focus);
      outline-offset: -2px;
    }
    .flag {
      width: 20px;
      height: 15px;
      object-fit: cover;
      border-radius: 2px;
      display: block;
    }
    .caret {
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid currentColor;
      opacity: 0.6;
    }

    input {
      flex: 1;
      height: 36px;
      padding: 0 12px;
      border: 0;
      background: transparent;
      font: inherit;
      color: inherit;
      outline: none;
      min-width: 0;
    }
    input::placeholder {
      color: var(--lp-text-muted);
    }

    .helper {
      margin-top: 6px;
      font-size: 12px;
      color: var(--lp-text-muted);
    }
    :host([error]) .helper {
      color: var(--lp-border-error);
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      right: 0;
      max-height: 320px;
      overflow: hidden;
      background: #fff;
      border: 1px solid var(--lp-border);
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      z-index: 100;
      display: flex;
      flex-direction: column;
    }
    .search {
      padding: 8px;
      border-bottom: 1px solid var(--lp-border);
    }
    .search input {
      width: 100%;
      height: 32px;
      padding: 0 10px;
      border: 1px solid var(--lp-border);
      border-radius: 6px;
      background: #fff;
      font: inherit;
    }
    .list {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0;
    }
    .item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 12px;
      cursor: pointer;
      user-select: none;
    }
    .item:hover,
    .item.active {
      background: var(--lp-bg-hover);
    }
    .item .flag {
      width: 22px;
      height: 16px;
    }
    .item .cname {
      flex: 1;
    }
    .item .code {
      color: var(--lp-text-muted);
      font-variant-numeric: tabular-nums;
    }
    .empty {
      padding: 16px;
      text-align: center;
      color: var(--lp-text-muted);
    }
  `;

  /** E.164 phone number (controlled value). */
  @property({ type: String }) value = '';

  /** ISO2 country code. Defaults to 'tw'. */
  @property({ type: String }) country = 'tw';

  /** Fallback placeholder for the input. */
  @property({ type: String }) placeholder = '';

  /** When true, renders in error state. */
  @property({ type: Boolean, reflect: true }) error = false;

  /** Disables the input. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Helper text rendered below the input. */
  @property({ type: String, attribute: 'helper-text' }) helperText = '';

  /** Name attribute forwarded to the inner input (for form submission). */
  @property({ type: String }) name = '';

  /** Restrict country list to these iso2 codes (comma-separated, or set via property). */
  @property({ type: Array }) countries?: string[];

  @state() private _open = false;
  @state() private _inputDisplay = '';
  @state() private _search = '';

  @query('input.phone') private _inputEl!: HTMLInputElement;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onDocKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onDocKeydown);
  }

  override willUpdate(changed: PropertyValues<this>): void {
    if (changed.has('value') || changed.has('country')) {
      const next = this._deriveDisplay(this.value, this.country);
      // Avoid re-deriving over our own input-originated sync (see _onInput).
      if (next !== this._inputDisplay) {
        this._inputDisplay = next;
      }
    }
  }

  private _deriveDisplay(value: string, country: string): string {
    if (!value) return '';
    // If value is E.164 and the country matches, strip dial code; else just format.
    const c = getCountry(country);
    if (c && value.startsWith(`+${c.dialCode}`)) {
      const national = value.slice(1 + c.dialCode.length);
      return formatAsYouType(national, country);
    }
    return formatAsYouType(value, country);
  }

  private get _filteredCountries(): readonly Country[] {
    const allow = this.countries?.map((s) => s.toLowerCase());
    const source = allow
      ? COUNTRIES.filter((c) => allow.includes(c.iso2))
      : COUNTRIES;
    const q = this._search.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.includes(q) ||
        c.dialCode.includes(q.replace(/^\+/, '')),
    );
  }

  private _emit(): void {
    const inputValue = this._inputDisplay;
    const phone = toE164(inputValue, this.country);
    const isValid = isValidE164(phone);
    const detail: PhoneInputChangeDetail = {
      phone,
      country: this.country,
      inputValue,
      isValid,
    };
    this.dispatchEvent(
      new CustomEvent<PhoneInputChangeDetail>('change', {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onInput = (e: Event): void => {
    const raw = (e.target as HTMLInputElement).value;
    this._inputDisplay = formatAsYouType(raw, this.country);
    // Keep .value in sync so vanilla HTML consumers can read it like a normal <input>.
    this.value = toE164(this._inputDisplay, this.country);
    this._emit();
  };

  private _onFlagClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (this.disabled) return;
    this._open = !this._open;
    this._search = '';
    if (this._open) {
      // Focus the search box after render
      void this.updateComplete.then(() => {
        const s = this.renderRoot.querySelector<HTMLInputElement>(
          '.search input',
        );
        s?.focus();
      });
    }
  };

  private _onPickCountry = (iso2: string): void => {
    this.country = iso2;
    this._open = false;
    this._inputDisplay = this._deriveDisplay(this.value, iso2);
    this._emit();
    void this.updateComplete.then(() => this._inputEl?.focus());
  };

  private _onDocClick = (e: MouseEvent): void => {
    if (!this._open) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this._open = false;
    }
  };

  private _onDocKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._open = false;
    }
  };

  override render() {
    const c = getCountry(this.country) ?? COUNTRIES[0]!;
    const placeholder =
      this.placeholder || this._placeholderForCountry(this.country);

    return html`
      <div class="wrap">
        <button
          type="button"
          class="flag-btn"
          aria-haspopup="listbox"
          aria-expanded=${this._open}
          aria-label="Select country, current: ${c.name}"
          @click=${this._onFlagClick}
          ?disabled=${this.disabled}
        >
          <img
            class="flag"
            src=${flagUrl(c.iso2)}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
          <span class="caret" aria-hidden="true"></span>
        </button>
        <input
          class="phone"
          type="tel"
          autocomplete="tel-national"
          inputmode="tel"
          name=${this.name || nothing}
          .value=${this._inputDisplay}
          placeholder=${placeholder}
          ?disabled=${this.disabled}
          @input=${this._onInput}
        />
      </div>
      ${this.helperText
        ? html`<div class="helper">${this.helperText}</div>`
        : nothing}
      ${this._open ? this._renderDropdown() : nothing}
    `;
  }

  private _renderDropdown() {
    const list = this._filteredCountries;
    return html`
      <div class="dropdown" role="listbox">
        <div class="search">
          <input
            type="text"
            placeholder="Search country or dial code…"
            .value=${this._search}
            @input=${(e: Event) =>
              (this._search = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter' && list.length > 0) {
                e.preventDefault();
                this._onPickCountry(list[0]!.iso2);
              }
            }}
          />
        </div>
        <div class="list">
          ${list.length === 0
            ? html`<div class="empty">No matches</div>`
            : list.map(
                (country) => html`
                  <div
                    class=${classMap({
                      item: true,
                      active: country.iso2 === this.country,
                    })}
                    role="option"
                    aria-selected=${country.iso2 === this.country}
                    @click=${() => this._onPickCountry(country.iso2)}
                  >
                    <img
                      class="flag"
                      src=${flagUrl(country.iso2)}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                    />
                    <span class="cname">${country.name}</span>
                    <span class="code">+${country.dialCode}</span>
                  </div>
                `,
              )}
        </div>
      </div>
    `;
  }

  private _placeholderForCountry(iso2: string): string {
    const c = getCountry(iso2);
    if (!c) return '';
    // Simple, country-agnostic placeholder
    return `+${c.dialCode} xxx xxx xxx`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lightup-phone-input': LightupPhoneInput;
  }
}
